/**
 * Direct HTTP to whatsapp-api-flow (Echo). Responses use { success, data }; we normalize to the
 * same shape as CRM postRequest/getRequest ({ error, data, message, response_code }).
 */
import axios from "axios";
import Constants from "../config/constants";

function flowBaseUrl() {
  const u = (Constants.WHATSAPP_FLOW_URL || "").trim().replace(/\/$/, "");
  return u || null;
}

function flowHeadersJson() {
  const h = {
    "Content-Type": "application/json",
    "X-Timezone": String(Constants.TIME_ZONE ?? 5.5),
  };
  const token = (Constants.WHATSAPP_FLOW_TOKEN || "").trim();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

function flowHeadersMultipart() {
  const h = { "X-Timezone": String(Constants.TIME_ZONE ?? 5.5) };
  const token = (Constants.WHATSAPP_FLOW_TOKEN || "").trim();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

function errorMessage(status, body) {
  const d = body?.data;
  if (d && typeof d === "object") {
    if (d.details) return String(d.details);
    if (d.error) return String(d.error);
  }
  if (body?.message) return String(body.message);
  return `WhatsApp API ${status}`;
}

/** @returns {{ error: boolean, data?: any, message?: string, authorization?: boolean, response_code?: number }} */
function normalizeSuccess(data) {
  return {
    error: false,
    data,
    message: "",
    authorization: true,
    response_code: 1,
  };
}

function normalizeFailure(message) {
  return { error: true, message: message || "Request failed", authorization: true, response_code: 0 };
}

export function flowConfigured() {
  return !!flowBaseUrl();
}

export async function flowPost(path, body = {}) {
  const base = flowBaseUrl();
  if (!base) {
    return normalizeFailure("Set REACT_APP_WHATSAPP_FLOW_URL to your whatsapp-api-flow base URL");
  }
  try {
    const res = await axios.post(`${base}${path}`, body, {
      headers: flowHeadersJson(),
      validateStatus: () => true,
    });
    const payload = res.data;
    if (res.status >= 200 && res.status < 300 && payload?.success) {
      return normalizeSuccess(payload.data);
    }
    return normalizeFailure(errorMessage(res.status, payload));
  } catch (err) {
    const st = err?.response?.status;
    const body = err?.response?.data;
    return normalizeFailure(errorMessage(st, body) || err.message || "Network error");
  }
}

export async function flowGet(path) {
  const base = flowBaseUrl();
  if (!base) {
    return normalizeFailure("Set REACT_APP_WHATSAPP_FLOW_URL to your whatsapp-api-flow base URL");
  }
  try {
    const res = await axios.get(`${base}${path}`, {
      headers: flowHeadersJson(),
      validateStatus: () => true,
    });
    const payload = res.data;
    if (res.status >= 200 && res.status < 300 && payload?.success) {
      return normalizeSuccess(payload.data);
    }
    return normalizeFailure(errorMessage(res.status, payload));
  } catch (err) {
    const st = err?.response?.status;
    const body = err?.response?.data;
    return normalizeFailure(errorMessage(st, body) || err.message || "Network error");
  }
}

export async function flowPut(path, body = {}) {
  const base = flowBaseUrl();
  if (!base) {
    return normalizeFailure("Set REACT_APP_WHATSAPP_FLOW_URL to your whatsapp-api-flow base URL");
  }
  try {
    const res = await axios.put(`${base}${path}`, body, {
      headers: flowHeadersJson(),
      validateStatus: () => true,
    });
    const payload = res.data;
    if (res.status >= 200 && res.status < 300 && payload?.success) {
      return normalizeSuccess(payload.data);
    }
    return normalizeFailure(errorMessage(res.status, payload));
  } catch (err) {
    const st = err?.response?.status;
    const body = err?.response?.data;
    return normalizeFailure(errorMessage(st, body) || err.message || "Network error");
  }
}

export async function flowDelete(path) {
  const base = flowBaseUrl();
  if (!base) {
    return normalizeFailure("Set REACT_APP_WHATSAPP_FLOW_URL to your whatsapp-api-flow base URL");
  }
  try {
    const res = await axios.delete(`${base}${path}`, {
      headers: flowHeadersJson(),
      validateStatus: () => true,
    });
    const payload = res.data;
    if (res.status >= 200 && res.status < 300 && payload?.success) {
      return normalizeSuccess(payload.data);
    }
    return normalizeFailure(errorMessage(res.status, payload));
  } catch (err) {
    const st = err?.response?.status;
    const body = err?.response?.data;
    return normalizeFailure(errorMessage(st, body) || err.message || "Network error");
  }
}

export async function flowPostMultipart(path, formData) {
  const base = flowBaseUrl();
  if (!base) {
    return normalizeFailure("Set REACT_APP_WHATSAPP_FLOW_URL to your whatsapp-api-flow base URL");
  }
  try {
    const res = await axios.post(`${base}${path}`, formData, {
      headers: flowHeadersMultipart(),
      validateStatus: () => true,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
    const payload = res.data;
    if (res.status >= 200 && res.status < 300 && payload?.success) {
      return normalizeSuccess(payload.data);
    }
    return normalizeFailure(errorMessage(res.status, payload));
  } catch (err) {
    const st = err?.response?.status;
    const body = err?.response?.data;
    return normalizeFailure(errorMessage(st, body) || err.message || "Network error");
  }
}
